import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import {
  Plus, Trash2, Edit3, Radio, Type, Calendar, Key,
  Webhook, Settings, Eye, EyeOff, Copy, Check,
  AlertTriangle, Wifi, Play, Globe, Send, TestTube, RefreshCw
} from 'lucide-react'
import { format } from 'date-fns'
import {
  schedulesApi, tickersApi, streamsApi, playlistsApi,
  screensApi, groupsApi, apiKeysApi, webhooksApi, smtpApi
} from '../services/api'
import { useAlertStore } from '@/stores/stores'
import {
  AppShell, Topbar, Modal, ConfirmDialog, StatusBadge,
  PageLoader, EmptyState, ToastContainer, Spinner
} from '../components/layout/Layout'
import { useForm as useFormRHF } from 'react-hook-form'

// ══════════════════════════════════════════════════════════════
// SCHEDULES PAGE
// ══════════════════════════════════════════════════════════════

// ── ScreenMultiSelect – výběr obrazovek seskupených podle tenanta ──
function ScreenMultiSelect({ screens, value, onChange }) {
  // Seskupit podle tenanta
  const byTenant = screens.reduce((acc, s) => {
    const key = s.tenant?.name ?? `Tenant #${s.tenant_id}`
    if (!acc[key]) acc[key] = []
    acc[key].push(s)
    return acc
  }, {})

  const toggle = (id) => {
    const numId = Number(id)
    onChange(value.includes(numId)
      ? value.filter(v => v !== numId)
      : [...value, numId]
    )
  }

  const toggleAll = (screenIds) => {
    const numIds = screenIds.map(Number)
    const allSelected = numIds.every(id => value.includes(id))
    if (allSelected) {
      onChange(value.filter(v => !numIds.includes(v)))
    } else {
      onChange([...new Set([...value, ...numIds])])
    }
  }

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
      {Object.entries(byTenant).map(([tenantName, tenantScreens]) => (
        <div key={tenantName}>
          {/* Tenant header */}
          <div
            onClick={() => toggleAll(tenantScreens.map(s => s.id))}
            style={{
              padding: '6px 12px',
              background: 'var(--bg-elevated)',
              fontSize: '0.72rem',
              fontWeight: 600,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              borderBottom: '1px solid var(--border)',
            }}
          >
            <input
              type="checkbox"
              readOnly
              checked={tenantScreens.every(s => value.includes(Number(s.id)))}
              style={{ cursor: 'pointer' }}
            />
            {tenantName} ({tenantScreens.length})
          </div>
          {/* Obrazovky */}
          {tenantScreens.filter(s => s.status !== 'pending').map(s => (
            <div
              key={s.id}
              onClick={() => toggle(s.id)}
              style={{
                padding: '8px 12px 8px 28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: value.includes(Number(s.id)) ? 'var(--accent-dim)' : 'transparent',
                borderBottom: '1px solid var(--border)',
                fontSize: '0.82rem',
              }}
            >
              <input type="checkbox" readOnly checked={value.includes(Number(s.id))} style={{ cursor: 'pointer' }} />
              <span style={{ flex: 1 }}>{s.name}</span>
              <span style={{
                fontSize: '0.68rem',
                padding: '2px 6px',
                borderRadius: 4,
                background: s.status === 'active' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                color: s.status === 'active' ? 'var(--green)' : 'var(--red)',
              }}>
                {s.status === 'active' ? 'Online' : s.status}
              </span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>{s.resolution}</span>
            </div>
          ))}
        </div>
      ))}
      {screens.length === 0 && (
        <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: '0.82rem', textAlign: 'center' }}>
          Žádné obrazovky k dispozici
        </div>
      )}
      {value.length > 0 && (
        <div style={{ padding: '6px 12px', background: 'var(--accent-dim)', fontSize: '0.78rem', color: 'var(--accent-light)' }}>
          Vybráno: {value.length} obrazovek
        </div>
      )}
    </div>
  )
}

export function SchedulesPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteId, setDeleteId]     = useState(null)
  const [editItem, setEditItem]     = useState(null)
  const { addToast } = useAlertStore()
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({defaultValues: { target_type: 'screen', valid_to: '2050-01-01T23:59' }})

  const { data: schedules, isLoading } = useQuery({
    queryKey: ['schedules'],
    queryFn: () => schedulesApi.list().then(r => r.data),
  })
  const { data: playlists } = useQuery({
    queryKey: ['playlists'],
    queryFn: () => playlistsApi.list().then(r => r.data),
  })
  const { data: screens } = useQuery({
    queryKey: ['screens'],
    queryFn: () => screensApi.list().then(r => r.data),
  })
  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupsApi.list().then(r => r.data),
  })

  const targetType = watch('target_type', 'screen')
  const [selectedScreens, setSelectedScreens] = useState([])

  const create = async (data) => {
    // Validace povinných polí
    if (!data.playlist_id) { addToast({ type: 'error', title: 'Vyberte playlist' }); return }
    if (data.target_type === 'screen' && selectedScreens.length === 0) {
      addToast({ type: 'error', title: 'Vyberte alespoň jednu obrazovku' }); return
    }
    if (data.target_type === 'group' && !data.target_id) {
      addToast({ type: 'error', title: 'Vyberte skupinu' }); return
    }
    // Výchozí valid_to
    if (!data.valid_to) data.valid_to = '2050-01-01T23:59'
    // Parsování opakování
    const payload = {
      ...data,
      repeat_months:   data.repeat_months   ? data.repeat_months.split(',').map(Number).filter(Boolean) : null,
      repeat_weekdays: data.repeat_weekdays  ? data.repeat_weekdays.split(',').map(Number).filter(Boolean) : null,
      repeat_hours:    data.repeat_hours     ? data.repeat_hours.split(',').map(Number).filter(Boolean) : null,
      is_priority:     data.is_priority === 'true' || data.is_priority === true,
    }
    if (data.target_type === 'screen' && selectedScreens.length > 0) {
      // Vytvořit schedule pro každou vybranou obrazovku
      await Promise.all(selectedScreens.map(screenId =>
        schedulesApi.create({ ...payload, target_type: 'screen', target_id: screenId })
      ))
      addToast({ type: 'success', title: `Plánování vytvořeno pro ${selectedScreens.length} obrazovek` })
    } else {
      await schedulesApi.create(payload)
      addToast({ type: 'success', title: 'Plánování vytvořeno' })
    }
    qc.invalidateQueries(['schedules'])
    setCreateOpen(false)
    setSelectedScreens([])
    reset()
  }

  const openEdit = (s) => {
    setEditItem(s)
    setValue('playlist_id', s.playlist_id)
    setValue('target_type', s.target_type)
    setValue('target_id',   s.target_id)
    setValue('valid_from',  s.valid_from ? s.valid_from.slice(0,16) : '')
    setValue('valid_to',    s.valid_to   ? s.valid_to.slice(0,16)   : '2050-01-01T23:59')
    setValue('is_priority', s.is_priority)
    setValue('active',      s.active)
    setCreateOpen(true)
  }

  const saveSchedule = async (data) => {
    if (!data.playlist_id) { addToast({ type: 'error', title: 'Vyberte playlist' }); return }
    const payload = {
      ...data,
      repeat_months:   data.repeat_months   ? data.repeat_months.split(',').map(Number).filter(Boolean)   : null,
      repeat_weekdays: data.repeat_weekdays  ? data.repeat_weekdays.split(',').map(Number).filter(Boolean) : null,
      repeat_hours:    data.repeat_hours     ? data.repeat_hours.split(',').map(Number).filter(Boolean)    : null,
      is_priority:     data.is_priority === 'true' || data.is_priority === true,
    }
    await schedulesApi.update(editItem.id, payload)
    qc.invalidateQueries(['schedules'])
    addToast({ type: 'success', title: 'Plánování aktualizováno' })
    setCreateOpen(false)
    setEditItem(null)
    reset()
  }

  const deleteSchedule = async (id) => {
    await schedulesApi.delete(id)
    qc.invalidateQueries(['schedules'])
    addToast({ type: 'success', title: 'Plánování smazáno' })
  }

  const toggleActive = async (s) => {
    await schedulesApi.update(s.id, { active: !s.active })
    qc.invalidateQueries(['schedules'])
  }

  const weekdayLabels = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']
  const monthLabels   = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro']

  return (
    <AppShell>
      <Topbar
        title="Plánování"
        subtitle="Kdy hrát který playlist"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> Nové plánování
          </button>
        }
      />
      <main className="app-main">
        {isLoading ? <PageLoader /> : schedules?.length > 0 ? (
          <div className="card" style={{ padding: 0 }}>
            <div className="table-wrapper">
              <table className="table">
                <thead>
                  <tr>
                    <th>Playlist</th>
                    <th>Cíl</th>
                    <th>Priorita</th>
                    <th>Platnost</th>
                    <th>Opakování</th>
                    <th>Stav</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map(s => (
                    <tr key={s.id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{s.playlist?.name}</div>
                        {s.name && <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{s.name}</div>}
                      </td>
                      <td>
                        <span className="badge badge-info">
                          {s.target_type === 'screen' ? '🖥 ' : '📁 '}
                          {s.target_type} #{s.target_id}
                        </span>
                      </td>
                      <td>
                        {s.is_priority
                          ? <span className="badge badge-offline">⚡ Priorita {s.priority_level}</span>
                          : <span className="badge badge-info">Normal</span>
                        }
                      </td>
                      <td style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                        {s.valid_from ? format(new Date(s.valid_from), 'dd.MM.yy') : '∞'}
                        {' – '}
                        {s.valid_to ? format(new Date(s.valid_to), 'dd.MM.yy') : '∞'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                          {s.repeat_weekdays?.map(d => (
                            <span key={d} style={{
                              background: 'var(--accent-dim)', color: 'var(--accent-light)',
                              borderRadius: 3, padding: '0 4px', fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
                            }}>{weekdayLabels[d - 1]}</span>
                          ))}
                          {s.repeat_hours?.map(h => (
                            <span key={h} style={{
                              background: 'var(--purple-dim)', color: 'var(--purple)',
                              borderRadius: 3, padding: '0 4px', fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
                            }}>{String(h).padStart(2, '0')}:00</span>
                          ))}
                          {!s.repeat_weekdays?.length && !s.repeat_hours?.length && (
                            <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>Vždy</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <label className="toggle-switch" title={s.active ? 'Aktivní' : 'Neaktivní'}>
                          <input type="checkbox" checked={s.active} onChange={() => toggleActive(s)} />
                          <span className="toggle-slider" />
                        </label>
                      </td>
                      <td>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => openEdit(s)}><Edit3 size={14} /></button>
                        <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(s.id)}
                          style={{ color: 'var(--red)' }}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Calendar}
            title="Žádné plánování"
            text="Přiřaďte playlist ke konkrétní obrazovce nebo skupině"
            action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Vytvořit plánování</button>}
          />
        )}
      </main>

      <Modal
        open={createOpen}
        onClose={() => { setCreateOpen(false); setEditItem(null); reset() }}
        title={editItem ? 'Upravit plánování' : 'Nové plánování'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button>
            <button className="btn btn-primary" onClick={handleSubmit(editItem ? saveSchedule : create)}>Uložit</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label" style={{ color: 'var(--red)' }}>Playlist *</label>
          <select {...register('playlist_id', { required: true })} className="form-select"
            style={{ borderColor: errors.playlist_id ? 'var(--red)' : undefined }}>
            <option value="">-- Vyberte playlist --</option>
            {playlists?.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {errors.playlist_id && <div style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: 4 }}>Playlist je povinný</div>}
        </div>

        {/* Cíl - výběr obrazovek seskupených podle tenanta */}
        <div className="form-group">
          <label className="form-label">Typ cíle</label>
          <select {...register('target_type')} className="form-select">
            <option value="screen">Obrazovka(y)</option>
            <option value="group">Skupina obrazovek</option>
          </select>
        </div>
        {targetType === 'screen' ? (
          <div className="form-group">
            <label className="form-label" style={{ color: 'var(--red)' }}>
              Obrazovky * <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem', fontWeight: 400 }}>(klikněte pro výběr, Ctrl+klik pro více)</span>
            </label>
            <ScreenMultiSelect
              screens={screens ?? []}
              value={selectedScreens}
              onChange={setSelectedScreens}
            />
            {selectedScreens.length === 0 && <div style={{ color: 'var(--red)', fontSize: '0.75rem', marginTop: 4 }}>Vyberte alespoň jednu obrazovku</div>}
          </div>
        ) : (
          <div className="form-group">
            <label className="form-label">Skupina</label>
            <select {...register('target_id')} className="form-select">
              <option value="">-- Vyberte skupinu --</option>
              {groups?.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Platnost od</label>
            <input {...register('valid_from')} type="datetime-local" className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Platnost do</label>
            <input {...register('valid_to')} type="datetime-local" className="form-input" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Dny v týdnu (čísla 1-7, oddělená čárkou, 1=Pondělí)</label>
          <input {...register('repeat_weekdays')} className="form-input" placeholder="1,2,3,4,5 = Pondělí–Pátek" />
          <div className="form-hint">Prázdné = každý den</div>
        </div>

        <div className="form-group">
          <label className="form-label">Hodiny (0-23, oddělené čárkou)</label>
          <input {...register('repeat_hours')} className="form-input" placeholder="8,12,17 = v 8:00, 12:00 a 17:00" />
          <div className="form-hint">Prázdné = celý den</div>
        </div>

        <div className="form-group">
          <label className="form-label">Priorita</label>
          <select {...register('is_priority')} className="form-select">
            <option value="false">Normální</option>
            <option value="true">Prioritní (přeruší aktuální playlist)</option>
          </select>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteSchedule(deleteId)}
        title="Smazat plánování"
        message="Plánování bude odstraněno."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

// ══════════════════════════════════════════════════════════════
// TICKERS PAGE
// ══════════════════════════════════════════════════════════════
export function TickersPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const { addToast } = useAlertStore()
  const { register, handleSubmit, watch, reset } = useForm({
    defaultValues: { bg_color: '#1F4E79', text_color: '#FFFFFF', speed: 50, position: 'bottom' }
  })

  const bgColor    = watch('bg_color', '#1F4E79')
  const textColor  = watch('text_color', '#FFFFFF')
  const targetType = watch('target_type', 'all')
  const { data: screens2 = [] } = useQuery({ queryKey: ['screens-t'], queryFn: () => screensApi.list().then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []) })
  const { data: playlists2 = [] } = useQuery({ queryKey: ['playlists-t'], queryFn: () => playlistsApi.list().then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []) })

  const { data: tickers, isLoading } = useQuery({
    queryKey: ['tickers'],
    queryFn: () => tickersApi.list().then(r => r.data),
  })

  const [messages, setMessages] = useState([{ text: '', icon: '' }])

  const create = async (data) => {
    // Převést target_type + target_id na targets pole které backend očekává
    const targets = data.target_type === 'all'
      ? []
      : [{ type: data.target_type, id: parseInt(data.target_id) }]
    const { target_type, target_id, ...rest } = data
    await tickersApi.create({ ...rest, messages: messages.filter(m => m.text), targets })
    qc.invalidateQueries(['tickers'])
    addToast({ type: 'success', title: 'Ticker vytvořen' })
    setCreateOpen(false)
    reset()
    setMessages([{ text: '', icon: '' }])
  }

  const toggleActive = async (ticker) => {
    await tickersApi.update(ticker.id, { active: !ticker.active })
    qc.invalidateQueries(['tickers'])
  }

  return (
    <AppShell>
      <Topbar
        title="Tickery"
        subtitle="Scrollující zprávy přes obrazovky"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> Nový ticker
          </button>
        }
      />
      <main className="app-main">
        {isLoading ? <PageLoader /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {tickers?.length === 0 && (
              <EmptyState icon={Type} title="Žádné tickery" text="Vytvořte scrollující zprávu pro vaše obrazovky" />
            )}
            {tickers?.map(ticker => (
              <div key={ticker.id} className="card">
                {/* Preview */}
                <div className="ticker-preview mb-4" style={{ background: ticker.bg_color }}>
                  <div className="ticker-preview-logo" style={{ background: ticker.logo_bg_color ?? ticker.bg_color, color: ticker.text_color }}>
                    📺
                  </div>
                  <div className="ticker-preview-scroll">
                    <div className="ticker-preview-text" style={{ color: ticker.text_color }}>
                      {ticker.messages_count > 0
                        ? `${ticker.separator_icon} Zpráva 1 ${ticker.separator_icon} Zpráva 2 ${ticker.separator_icon} Zpráva 3`
                        : 'Žádné zprávy'
                      }
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div style={{ fontWeight: 600 }}>{ticker.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                      {ticker.messages_count} zpráv · rychlost {ticker.speed}
                      {ticker.target_type && ticker.target_type !== 'all' && ` · ${ticker.target_type} #${ticker.target_id}`}
                      {ticker.valid_from && ` · od ${format(new Date(ticker.valid_from), 'dd.MM.yy')}`}
                      {ticker.valid_to   && ` do ${format(new Date(ticker.valid_to),   'dd.MM.yy')}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="toggle-switch">
                      <input type="checkbox" checked={ticker.active} onChange={() => toggleActive(ticker)} />
                      <span className="toggle-slider" />
                    </label>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(ticker.id)}
                      style={{ color: 'var(--red)' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nový ticker"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button>
            <button className="btn btn-primary" onClick={handleSubmit(create)}>Vytvořit</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Název tickeru</label>
          <input {...register('name')} className="form-input" placeholder="Denní zprávy" autoFocus />
        </div>

        <div className="form-row form-row-3">
          <div className="form-group">
            <label className="form-label">Barva pozadí</label>
            <div className="flex items-center gap-2">
              <input {...register('bg_color')} type="color" style={{ width: 36, height: 36, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }} />
              <input {...register('bg_color')} className="form-input" placeholder="#1F4E79" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Barva textu</label>
            <div className="flex items-center gap-2">
              <input {...register('text_color')} type="color" style={{ width: 36, height: 36, border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer' }} />
              <input {...register('text_color')} className="form-input" placeholder="#FFFFFF" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Rychlost (px/s)</label>
            <input {...register('speed')} type="number" className="form-input" min={10} max={200} />
          </div>
        </div>

        {/* Live preview */}
        <div className="ticker-preview" style={{ background: bgColor }}>
          <div className="ticker-preview-logo" style={{ color: textColor }}>📺</div>
          <div className="ticker-preview-scroll">
            <div className="ticker-preview-text" style={{ color: textColor }}>
              {messages.map((m, i) => m.text ? `${m.icon || '•'} ${m.text}` : '').filter(Boolean).join('   ') || '• Náhled tickeru'}
            </div>
          </div>
        </div>

        {/* Messages */}
        <div>
          <div className="form-label mb-2">Zprávy</div>
          {messages.map((msg, i) => (
            <div key={i} className="flex items-center gap-2 mb-2">
              <input
                value={msg.icon} onChange={e => setMessages(prev => prev.map((m, j) => j === i ? { ...m, icon: e.target.value } : m))}
                className="form-input" placeholder="🔥" style={{ width: 60 }}
              />
              <input
                value={msg.text} onChange={e => setMessages(prev => prev.map((m, j) => j === i ? { ...m, text: e.target.value } : m))}
                className="form-input" placeholder="Text zprávy..."
              />
              {messages.length > 1 && (
                <button className="btn btn-ghost btn-icon" onClick={() => setMessages(prev => prev.filter((_, j) => j !== i))}
                  style={{ color: 'var(--red)', flexShrink: 0 }}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          ))}
          <button className="btn btn-secondary btn-sm" onClick={() => setMessages(prev => [...prev, { text: '', icon: '' }])}>
            <Plus size={12} /> Přidat zprávu
          </button>
        </div>

        {/* Cíl zobrazení */}
        <div className="form-group">
          <label className="form-label">Zobrazit na</label>
          <select {...register('target_type')} className="form-select">
            <option value="all">Všechny obrazovky</option>
            <option value="screen">Konkrétní obrazovka</option>
            <option value="playlist">Konkrétní playlist</option>
          </select>
        </div>
        {targetType === 'screen' && (
          <div className="form-group">
            <label className="form-label">Obrazovka</label>
            <select {...register('target_id')} className="form-select">
              <option value="">-- Vyberte obrazovku --</option>
              {screens2.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        {targetType === 'playlist' && (
          <div className="form-group">
            <label className="form-label">Playlist</label>
            <select {...register('target_id')} className="form-select">
              <option value="">-- Vyberte playlist --</option>
              {playlists2.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}

        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Platnost od</label>
            <input {...register('valid_from')} type="datetime-local" className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Platnost do</label>
            <input {...register('valid_to')} type="datetime-local" className="form-input" />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => { await tickersApi.delete(deleteId); qc.invalidateQueries(['tickers']); addToast({ type: 'success', title: 'Ticker smazán' }) }}
        title="Smazat ticker"
        message="Ticker bude odstraněn ze všech obrazovek."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

// ══════════════════════════════════════════════════════════════
// STREAMS PAGE
// ══════════════════════════════════════════════════════════════
export function StreamsPage() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const { addToast } = useAlertStore()
  const { register, handleSubmit, reset } = useForm()

  const { data: streams, isLoading } = useQuery({
    queryKey: ['streams'],
    queryFn: () => streamsApi.list().then(r => r.data),
    refetchInterval: 10000,
  })

  const [m3uChannels, setM3uChannels] = useState({})
  const [m3uLoading,  setM3uLoading]  = useState({})

  const loadM3u = async (stream) => {
    setM3uLoading(p => ({ ...p, [stream.id]: true }))
    try {
      const res = await streamsApi.parseM3u(stream.id)
      setM3uChannels(p => ({ ...p, [stream.id]: res.data.channels }))
      qc.invalidateQueries(['streams'])
      addToast({ type: 'success', title: `Načteno ${res.data.count} kanálů` })
    } catch (e) {
      addToast({ type: 'error', title: 'Chyba načtení M3U' })
    }
    setM3uLoading(p => ({ ...p, [stream.id]: false }))
  }

  const selectChannel = async (streamId, index) => {
    await streamsApi.selectChannel(streamId, index)
    qc.invalidateQueries(['streams'])
    addToast({ type: 'success', title: 'Kanál přepnut' })
  }

  const create = async (data) => {
    await streamsApi.create(data)
    qc.invalidateQueries(['streams'])
    addToast({ type: 'success', title: 'Stream přidán – probíhá transkódování' })
    setCreateOpen(false)
    reset()
  }

  return (
    <AppShell>
      <Topbar
        title="Streamy"
        subtitle="RTSP · RTMP · HLS live streamy"
        actions={
          <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
            <Plus size={14} /> Přidat stream
          </button>
        }
      />
      <main className="app-main">
        {isLoading ? <PageLoader /> : streams?.length > 0 ? (
          <div className="grid-auto">
            {streams.map(stream => (
              <div key={stream.id} className="card">
                {/* HLS player preview */}
                {stream.hls_url && stream.status === 'ready' ? (
                  <div style={{ aspectRatio: '16/9', background: 'var(--bg-base)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Wifi size={32} color="var(--green)" />
                  </div>
                ) : (
                  <div style={{ aspectRatio: '16/9', background: 'var(--bg-base)', borderRadius: 'var(--radius-md)', overflow: 'hidden', marginBottom: 14, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    {stream.status === 'transcoding'
                      ? <><Spinner size={24} /><span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Transkóduji...</span></>
                      : <><Radio size={28} color="var(--text-muted)" /><span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{stream.type.toUpperCase()}</span></>
                    }
                  </div>
                )}

                <div style={{ fontWeight: 600, marginBottom: 4 }}>{stream.name}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginBottom: 10, wordBreak: 'break-all' }}>
                  {stream.url}
                </div>
                {/* M3U kanály */}
                {stream.type === 'm3u' && (
                  <div style={{ marginBottom: 10 }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => loadM3u(stream)}
                      disabled={m3uLoading[stream.id]}
                      style={{ marginBottom: 8, width: '100%' }}
                    >
                      {m3uLoading[stream.id] ? <Spinner size={12} /> : <RefreshCw size={12} />}
                      {' '}{m3uLoading[stream.id] ? 'Načítám...' : 'Načíst kanály'}
                    </button>
                    {(m3uChannels[stream.id] ?? (Array.isArray(stream.m3u_channels) ? stream.m3u_channels : null))?.length > 0 && (
                      <div style={{ maxHeight: 200, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                        {(m3uChannels[stream.id] ?? (Array.isArray(stream.m3u_channels) ? stream.m3u_channels : [])).map((ch, i) => (
                          <div
                            key={i}
                            onClick={() => selectChannel(stream.id, i)}
                            style={{
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '0.78rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 8,
                              background: stream.m3u_selected_index === i ? 'var(--accent-dim)' : 'transparent',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            {ch.logo && <img src={ch.logo} style={{ width: 20, height: 20, objectFit: 'contain' }} onError={e => e.target.style.display='none'} />}
                            <span style={{ flex: 1 }}>{ch.name}</span>
                            {ch.group && <span style={{ color: 'var(--text-muted)', fontSize: '0.68rem' }}>{ch.group}</span>}
                            {stream.m3u_selected_index === i && <span style={{ color: 'var(--accent)', fontSize: '0.68rem' }}>▶ aktivní</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <StatusBadge status={stream.status} />
                  <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setDeleteId(stream.id)}
                    style={{ color: 'var(--red)' }}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Radio}
            title="Žádné streamy"
            text="Přidejte RTSP/RTMP/HLS stream pro živé vysílání na obrazovkách"
            action={<button className="btn btn-primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Přidat stream</button>}
          />
        )}
      </main>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Přidat stream"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button>
            <button className="btn btn-primary" onClick={handleSubmit(create)}>Přidat</button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Název streamu</label>
          <input {...register('name')} className="form-input" placeholder="IP kamera – vchod" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">URL streamu</label>
          <input {...register('url')} className="form-input" placeholder="rtsp://192.168.1.100/stream" />
        </div>
        <div className="form-group">
          <label className="form-label">Typ</label>
          <select {...register('type')} className="form-select">
            <option value="rtsp">RTSP (IP kamery)</option>
            <option value="rtmp">RTMP (OBS, streaming)</option>
            <option value="hls">HLS (m3u8)</option>
            <option value="m3u">M3U / IPTV playlist</option>
          </select>
        </div>
        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Uživatelské jméno (volitelné)</label>
            <input {...register('username')} className="form-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Heslo (volitelné)</label>
            <input {...register('password')} type="password" className="form-input" />
          </div>
        </div>
        <div style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius-md)', padding: 12, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
          ℹ️ RTSP/RTMP streamy budou automaticky překódovány na HLS (FFmpeg). Může trvat 10–30 sekund.
        </div>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={async () => { await streamsApi.delete(deleteId); qc.invalidateQueries(['streams']); addToast({ type: 'success', title: 'Stream smazán' }) }}
        title="Smazat stream"
        message="Stream bude zastaven a odstraněn."
        danger
      />
      <ToastContainer />
    </AppShell>
  )
}

// ══════════════════════════════════════════════════════════════
// SETTINGS PAGE (SMTP + API klíče + webhooky)
// ══════════════════════════════════════════════════════════════
export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('smtp')
  const { addToast } = useAlertStore()

  const tabs = [
    { id: 'smtp',     label: 'SMTP / Email' },
    { id: 'apikeys',  label: 'API klíče' },
    { id: 'webhooks', label: 'Webhooky' },
  ]

  return (
    <AppShell>
      <Topbar title="Nastavení" />
      <main className="app-main">
        <div className="flex items-center gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`btn btn-sm ${activeTab === tab.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'smtp'     && <SmtpSettings addToast={addToast} />}
        {activeTab === 'apikeys'  && <ApiKeysSettings addToast={addToast} />}
        {activeTab === 'webhooks' && <WebhooksSettings addToast={addToast} />}
      </main>
      <ToastContainer />
    </AppShell>
  )
}

function SmtpSettings({ addToast }) {
  const qc = useQueryClient()
  const { register, handleSubmit, reset } = useForm()
  const [testing, setTesting] = useState(false)
  const [showPass, setShowPass] = useState(false)

  const { data: smtp } = useQuery({
    queryKey: ['smtp'],
    queryFn: () => smtpApi.get().then(r => r.data),
  })

  useEffect(() => { if (smtp) reset(smtp) }, [smtp])

  const save = async (data) => {
    await smtpApi.update(data)
    qc.invalidateQueries(['smtp'])
    addToast({ type: 'success', title: 'SMTP nastavení uloženo' })
  }

  const test = async () => {
    setTesting(true)
    try {
      await smtpApi.test()
      addToast({ type: 'success', title: 'Testovací e-mail odeslán' })
    } catch (e) {
      addToast({ type: 'error', title: 'SMTP test selhal', message: e.response?.data?.message })
    }
    setTesting(false)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">SMTP konfigurace</h3>
        <div className="flex items-center gap-2">
          <button className="btn btn-secondary btn-sm" onClick={test} disabled={testing}>
            {testing ? <Spinner size={14} /> : <Send size={14} />} Test e-mail
          </button>
          <button className="btn btn-primary btn-sm" onClick={handleSubmit(save)}>Uložit</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">SMTP Host</label>
            <input {...register('host')} className="form-input" placeholder="smtp.brevo.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Port</label>
            <select {...register('port')} className="form-select">
              <option value={25}>25 (bez ověření)</option>
              <option value={465}>465 (SSL/TLS)</option>
              <option value={587}>587 (STARTTLS)</option>
              <option value={2525}>2525 (alternativní)</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Šifrování</label>
          <select {...register('encryption')} className="form-select">
            <option value="">Žádné (port 25)</option>
            <option value="ssl">SSL</option>
            <option value="tls">TLS (STARTTLS)</option>
          </select>
        </div>

        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Uživatelské jméno</label>
            <input {...register('username')} className="form-input" placeholder="user@firma.cz" />
          </div>
          <div className="form-group">
            <label className="form-label">Heslo</label>
            <div className="flex items-center gap-2">
              <input {...register('password')} type={showPass ? 'text' : 'password'} className="form-input" />
              <button className="btn btn-ghost btn-icon" onClick={() => setShowPass(s => !s)} type="button">
                {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>
        </div>

        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Od (adresa)</label>
            <input {...register('from_address')} className="form-input" placeholder="noreply@firma.cz" />
          </div>
          <div className="form-group">
            <label className="form-label">Od (jméno)</label>
            <input {...register('from_name')} className="form-input" placeholder="Signage Platform" />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <label className="toggle-switch">
            <input {...register('active')} type="checkbox" />
            <span className="toggle-slider" />
          </label>
          <span style={{ fontSize: '0.8rem' }}>Použít vlastní SMTP místo výchozího</span>
        </div>
      </div>
    </div>
  )
}

function ApiKeysSettings({ addToast }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [newKey, setNewKey] = useState(null)
  const [copied, setCopied] = useState(false)
  const { register, handleSubmit, reset } = useForm()

  const { data: keys } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiKeysApi.list().then(r => r.data),
  })

  const create = async (data) => {
    const { data: res } = await apiKeysApi.create(data)
    qc.invalidateQueries(['api-keys'])
    setNewKey(res.key)
    setCreateOpen(false)
    reset()
  }

  const copy = async (text) => {
    await navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">API klíče</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Nový klíč
        </button>
      </div>

      {newKey && (
        <div style={{
          background: 'var(--green-dim)', border: '1px solid rgba(16,185,129,0.3)',
          borderRadius: 'var(--radius-md)', padding: 14, marginBottom: 16,
        }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--green)', marginBottom: 8 }}>
            ✅ API klíč vytvořen – uložte si ho, nebude znovu zobrazen!
          </div>
          <div className="flex items-center gap-2">
            <code style={{ flex: 1, fontFamily: 'var(--font-mono)', fontSize: '0.75rem', background: 'var(--bg-base)', padding: '6px 10px', borderRadius: 'var(--radius-sm)', wordBreak: 'break-all' }}>
              {newKey}
            </code>
            <button className="btn btn-secondary btn-sm" onClick={() => copy(newKey)}>
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr><th>Název</th><th>Klíč (prefix)</th><th>Naposledy použit</th><th>Stav</th><th></th></tr>
          </thead>
          <tbody>
            {keys?.map(k => (
              <tr key={k.id}>
                <td style={{ fontWeight: 500 }}>{k.name}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {k.key?.slice(0, 12)}...
                </td>
                <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString('cs') : 'Nikdy'}
                </td>
                <td><StatusBadge status={k.active ? 'active' : 'disabled'} /></td>
                <td>
                  <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--red)' }}
                    onClick={async () => { await apiKeysApi.delete(k.id); qc.invalidateQueries(['api-keys']); addToast({ type: 'success', title: 'Klíč smazán' }) }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nový API klíč"
        footer={<><button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button><button className="btn btn-primary" onClick={handleSubmit(create)}>Vytvořit</button></>}
      >
        <div className="form-group">
          <label className="form-label">Název klíče</label>
          <input {...register('name')} className="form-input" placeholder="ERP integrace" autoFocus />
        </div>
        <div className="form-row form-row-2">
          <div className="form-group">
            <label className="form-label">Rate limit (req/min)</label>
            <input {...register('rate_limit')} type="number" className="form-input" defaultValue={60} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">IP whitelist (volitelné, oddělené čárkou)</label>
          <input {...register('ip_whitelist')} className="form-input" placeholder="192.168.1.10, 10.0.0.1" />
        </div>
      </Modal>
    </div>
  )
}

function WebhooksSettings({ addToast }) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const { register, handleSubmit, reset } = useForm()

  const { data: hooks } = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => webhooksApi.list().then(r => r.data),
  })

  const allEvents = [
    'screen_offline', 'screen_online', 'screen_registered',
    'playlist_changed', 'priority_displayed', 'screenshot_taken',
    'media_uploaded', 'alert_created',
  ]

  const create = async (data) => {
    await webhooksApi.create({ ...data, events: data.events ?? [] })
    qc.invalidateQueries(['webhooks'])
    addToast({ type: 'success', title: 'Webhook vytvořen' })
    setCreateOpen(false)
    reset()
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Webhooky</h3>
        <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
          <Plus size={14} /> Nový webhook
        </button>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr><th>URL</th><th>Události</th><th>Poslední volání</th><th>Stav</th><th></th></tr>
          </thead>
          <tbody>
            {hooks?.map(h => (
              <tr key={h.id}>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', maxWidth: 200 }}>
                  <div className="truncate">{h.url}</div>
                </td>
                <td>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {h.events?.map(e => (
                      <span key={e} style={{
                        background: 'var(--accent-dim)', color: 'var(--accent-light)',
                        borderRadius: 3, padding: '1px 5px', fontSize: '0.62rem', fontFamily: 'var(--font-mono)',
                      }}>{e}</span>
                    ))}
                  </div>
                </td>
                <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {h.last_triggered_at ? new Date(h.last_triggered_at).toLocaleDateString('cs') : '—'}
                  {h.last_status_code && <span style={{ marginLeft: 6, color: h.last_status_code < 300 ? 'var(--green)' : 'var(--red)' }}>{h.last_status_code}</span>}
                </td>
                <td><StatusBadge status={h.active ? 'active' : 'disabled'} /></td>
                <td>
                  <button className="btn btn-ghost btn-sm btn-icon" style={{ color: 'var(--red)' }}
                    onClick={async () => { await webhooksApi.delete(h.id); qc.invalidateQueries(['webhooks']); addToast({ type: 'success', title: 'Webhook smazán' }) }}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {!hooks?.length && <tr><td colSpan={5}><EmptyState icon={Webhook} title="Žádné webhooky" /></td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nový webhook"
        footer={<><button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button><button className="btn btn-primary" onClick={handleSubmit(create)}>Vytvořit</button></>}
      >
        <div className="form-group">
          <label className="form-label">Callback URL</label>
          <input {...register('url')} className="form-input" placeholder="https://vase-app.cz/webhook" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Secret (volitelné – pro HMAC podpis)</label>
          <input {...register('secret')} className="form-input" placeholder="tajný klíč" />
        </div>
        <div className="form-group">
          <label className="form-label">Události</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {allEvents.map(e => (
              <label key={e} className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                <input {...register('events')} type="checkbox" value={e} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>{e}</span>
              </label>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  )
}
